import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, Modal, Animated, Easing } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useAuth } from '../../../context/Auth';
import { appAgentService } from '../../../services/central-api/app-agent';
import { InfoInRow } from '../../../components/InfoInRow';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import DrawnPlate from '../../../components/DrawnPlate';
import { ScrollView } from 'react-native-web-hover';
import DragAndZoom from '../../../components/DragAndZoom';
import Container from '../../../components/Container';

interface Props {
    detectionId: number;
    isModalVisible: boolean;
    setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}


export default function LprEventAlertModal({ detectionId, isModalVisible, setIsModalVisible }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const canvasRefDetection = useRef<HTMLCanvasElement>(null);

    const [detection, setDetection] = useState<LprDetection>();

    const borderAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(borderAnim, {
                    toValue: 1,
                    duration: 200,
                    easing: Easing.linear,
                    useNativeDriver: false,
                }),
                Animated.timing(borderAnim, {
                    toValue: 0,
                    duration: 200,
                    easing: Easing.linear,
                    useNativeDriver: false,
                }),
            ])
        ).start();
    }, []);

    const animatedBorderColor = borderAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['blue', 'red'],
    });

    function drawImageOnCanvas(detection: LprDetection) {
        const canvas = canvasRefDetection.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 4;

            const rect = {
                minX: Infinity,
                maxX: 0,
                minY: Infinity,
                maxY: 0
            };

            if (detection.platePosition) {
                for (const position of detection.platePosition) {
                    if (position.x < rect.minX) {
                        rect.minX = position.x;
                    }

                    if (position.x > rect.maxX) {
                        rect.maxX = position.x;
                    }

                    if (position.y < rect.minY) {
                        rect.minY = position.y;
                    }

                    if (position.y > rect.maxY) {
                        rect.maxY = position.y;
                    }
                }

                ctx.beginPath();
                ctx.moveTo(rect.minX, rect.minY);
                ctx.lineTo(rect.minX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.minY);
                ctx.closePath();
                ctx.stroke();
            }

        };

        image.src = detection.imageUrl;
    }

    async function playAlertSound() {
        if (window.AndroidBridge?.playAlertSound) {
            window.AndroidBridge.playAlertSound();
        }
    }

    async function stopAlertSound() {
        if (window.AndroidBridge?.stopAlertSound) {
            window.AndroidBridge.stopAlertSound();
        }
    }

    async function loadPageInfo(detectionId: number) {
        try {
            playAlertSound();

            const resp = await appAgentService.getAgentLprEvent(detectionId);
            setDetection(resp);
        } catch (err) {
            console.error(err);
        }
    }


    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo(detectionId);
        } else {
            stopAlertSound();
        }
    }, [isModalVisible]);

    useEffect(() => {
        if (detection) {
            const timeout = setTimeout(() => drawImageOnCanvas(detection), 100);
            return () => clearTimeout(timeout);
        }
    }, [detection]);

    if (!authenticatedUser || !detection) {
        return <></>;
    }

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => setIsModalVisible(false)}
            transparent={true}
        >
            <Container>
                <Animated.View style={[styles.card, { backgroundColor: animatedBorderColor }]}>
                    <ScrollView contentContainerStyle={{ flex: 1, gap: 10 }}>
                        <View style={{ alignItems: 'flex-end' }}>
                            <TouchableOpacity onPress={() => setIsModalVisible(false)} >
                                <FontAwesomeIcon fontSize={30} icon={faTimes as IconDefinition} color={theme.colors.iconColor} />
                            </TouchableOpacity>
                        </View>
                        <DragAndZoom>
                            <canvas key={detection.id} style={{ width: '100%' }} ref={canvasRefDetection} />
                        </DragAndZoom>
                        <View>
                            <DrawnPlate plate={detection.plate} highContrast={true} />
                        </View>
                        <View style={styles.detectionInformationContainer}>
                            <MyAppText style={styles.headerText(22)}>{translate('vehicleData')}</MyAppText>
                            <InfoInRow value={detection.description} title='description' />
                            <InfoInRow value={detection['vehicleDescription.brand']} title='brand' />
                            <InfoInRow value={detection['vehicleDescription.model']} title='model' />
                            <InfoInRow value={detection['vehicleDescription.color']} title='color' />
                            <InfoInRow value={detection['vehicleDescription.modelYear']} title='modelYear' />
                        </View>
                    </ScrollView>
                </Animated.View>
            </Container>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    headerContent: {
        flexDirection: 'row',
        minHeight: 40,
        alignItems: 'center',
        padding: 8,
    },
    headerText: (fontSize: number) => {
        return {
            fontSize,
            fontWeight: '600',
            color: theme.colors.neutral['500'],
        };
    },
    card: {
        borderWidth: 5,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    detectionIconsContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        gap: 24,
        flexDirection: 'row'
    },
    detectionInformationContainer: {
        gap: 16,
        padding: 8,
        backgroundColor: theme.colors.backgrounds['BG-1'],
        borderRadius: 8,
    }
}));
